import { Button, Spacing, useSnackbar } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { Box } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "components/lib/customModal";
import { useCreateSlots } from "../../../api/useCalendarTemplateQueries";
import { useInvalidateSlots } from "../hooks/useInvalidateSlots";
import { useWeekDate } from "../hooks/useWeekDate";
import { getISOWeek, getYear } from "date-fns";
import { CalendarTemplate } from "../../../api/types";

type Props = {
  selectedTemplate: CalendarTemplate | null;
  isOpen: boolean;
  onClose: () => void;
};

export const ConfirmApplyCalendarTemplateModal = ({
  selectedTemplate,
  isOpen,
  onClose,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const {
    mutate: applyTemplate,
    isPending: isApplying,
    isSuccess,
  } = useCreateSlots();
  const { date: activeCalendarDate } = useWeekDate();
  const invalidateSlots = useInvalidateSlots();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isSuccess) {
      invalidateSlots();
      handleClose();
      enqueueSnackbar({
        text: t("CALENDAR_WEEK_TEMPLATES_TEMPLATE_CREATED_SUCCESS"),
        isError: false,
      });
    }
  }, [isSuccess, invalidateSlots, handleClose, enqueueSnackbar, t]);

  return (
    <CustomModal.Wrapper
      onClose={() => {
        if (!isApplying) handleClose();
      }}
      open={isOpen}
      size="small"
      isScrollable={true}
    >
      <Typography insensitive variant="h3">
        {t("CALENDAR_WEEK_TEMPLATES_APPLY", {
          templateName: selectedTemplate?.name,
        })}
      </Typography>
      <Spacing b={24} />
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          variant="secondary"
          disabled={isApplying}
          onClick={() => handleClose()}
        >
          {t("CANCEL")}
        </Button>
        <Button
          variant="primary"
          disabled={isApplying}
          onClick={() =>
            applyTemplate({
              templateId: selectedTemplate!.id,
              targetWeek: `${getISOWeek(activeCalendarDate)}/${getYear(
                activeCalendarDate,
              )}`,
            })
          }
        >
          {t("PUBLISH_TEMPLATE_BUTTON")}
        </Button>
      </Box>
    </CustomModal.Wrapper>
  );
};
